import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { createStyles } from '@material-ui/core/styles';
import { HighlightOffRounded } from '@material-ui/icons';
import { FolderStructure } from '../../api-client/autogenerated';
import FolderStructureTree from '../custom-components/FolderStructureTree';
import CircularLoader from '../loader/CircularLoader';

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '26px',
      lineHeight: '30px',
      textAlign: 'left',
      color: '#0947B9',
      marginBottom: '8px',
      paddingLeft: 8,
    },
    rootIconButton: {
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    paperRoot: {
      minWidth: 500,
    },
    description: {
      fontStyle: 'italic',
      color: '#7A797A',
      marginLeft: 8,
      marginTop: 8,
    },
  }),
);

type DefaultFolderStructureDialogProps = {
  open: boolean;
  handleClose: () => void;
  /** The structure being previewed, or undefined when none is selected. */
  structure?: FolderStructure;
  /** Names the root folder. Falls back to a placeholder before the project has been named. */
  projectName?: string;
  isLoading?: boolean;
};

/**
 * Read-only view of one of the subscriber's default folder structures, shown from the new project
 * form so the structure can be checked before it is applied.
 */
export default function DefaultFolderStructureDialog(props: DefaultFolderStructureDialogProps) {
  const { open, handleClose, structure, projectName, isLoading } = props;
  const classes = useStyles();

  const getContent = () => {
    if (isLoading) return <CircularLoader />;
    if (!structure) {
      return (
        <Typography variant="body1" style={{ marginLeft: 8, marginTop: 16 }}>
          No folder structure has been set up for this subscriber. A subscriber admin can create one
          from the Subscriber Admin Dashboard.
        </Typography>
      );
    }
    return (
      <>
        {structure.description ? (
          <Typography variant="body1" className={classes.description}>
            {structure.description}
          </Typography>
        ) : null}
        <FolderStructureTree
          folders={structure.folders}
          projectName={projectName}
          style={{ marginTop: 16 }}
        />
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      PaperProps={{ classes: { root: classes.paperRoot } }}
    >
      <DialogTitle>
        <span className={classes.title}>{structure?.name || 'Folder Structure'}</span>
        <IconButton
          id="default-folder-structure-dialog-close-button"
          style={{ right: '16px', top: '16px', position: 'absolute' }}
          onClick={handleClose}
          classes={{ root: classes.rootIconButton }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingBottom: 24 }}>
        <Typography style={{ marginLeft: 8 }}>
          Folder structures can be edited on the Subscriber Admin Dashboard.
        </Typography>
        <Typography style={{ marginLeft: 8 }}>
          Once created, folders can be customized directly in the File Manager.
        </Typography>
        {getContent()}
      </DialogContent>
    </Dialog>
  );
}
